<script lang="ts" setup>
import i18next from 'i18next'
import posthog from 'posthog-js'
import type { SelectChangeEvent } from 'primevue/select'
import Select from 'primevue/select'

import { setI18nLanguage } from '@/i18n'

// Define props for the component
interface Props {
  showLanguageCode?: boolean
  // Off by default: most call sites want the bare flag, not a bordered select.
  dropdown?: boolean
}

// Define props with default value
const props = withDefaults(defineProps<Props>(), {
  showLanguageCode: true,
  dropdown: false,
})

const languages = [
  { name: 'English', languageCode: 'en', cdnCode: 'gb' },
  { name: 'Italiano', languageCode: 'it', cdnCode: 'it' },
  { name: 'French', languageCode: 'fr', cdnCode: 'fr' },
  { name: 'Español', languageCode: 'es', cdnCode: 'es' },
]

function getFlagUrl(countryCode: string): string {
  return `https://flagcdn.com/w160/${countryCode.toLowerCase()}.png`
}

async function handleLanguageChange(event: SelectChangeEvent) {
  const languageCode = event.value?.languageCode
  if (languageCode) {
    const from = i18next.language
    await setI18nLanguage(languageCode)
    posthog.capture('language_changed', { from, to: languageCode })
  }
}
</script>

<template>
  <div class="card flex justify-center" :class="{ bare: !dropdown }">
    <Select
      :class="{ 'w-full': dropdown }"
      :model-value="languages.find((lang) => lang.languageCode === $i18next.language)"
      :options="languages"
      option-label="name"
      @change="handleLanguageChange"
    >
      <template #value="slotProps">
        <div v-if="slotProps.value" class="flex items-center">
          <img
            :src="getFlagUrl(slotProps.value.cdnCode)"
            :alt="slotProps.value.name"
            class="w-5 h-4 mr-2"
          />
          <span v-if="dropdown">{{ slotProps.value.name }}</span>
          <span v-else-if="props.showLanguageCode">{{
            slotProps.value.languageCode.toUpperCase()
          }}</span>
        </div>
      </template>
      <template #option="slotProps">
        <div class="flex items-center">
          <img
            :alt="slotProps.option.name"
            :src="getFlagUrl(slotProps.option.cdnCode)"
            class="mr-2 w-5 h-4"
          />
          <div>{{ slotProps.option.name }}</div>
        </div>
      </template>
    </Select>
  </div>
</template>

<style scoped>
/* Bare mode keeps the chevron but drops the boxed-select chrome. */
.bare :deep(.p-select),
.bare :deep(.p-select:hover),
.bare :deep(.p-select:focus-within) {
  border: none;
  background: transparent;
  box-shadow: none;
}

.bare :deep(.p-select-trigger) {
  width: auto;
  padding-inline-start: 0.25rem;
}
</style>
